<template>
    <v-container fluid>
      <v-card elevation="2">
        <v-card-title class="d-flex align-center pe-2 tableheader white--text">
          <v-icon left color="white">mdi-account-group-outline</v-icon>
          Tenants Management
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            density="compact"
            label="Search Tenants (Name, Phone)"
            prepend-inner-icon="mdi-magnify"
            variant="solo-filled"
            flat
            hide-details
            single-line
            class="me-2 search-field"
          ></v-text-field>
          <v-btn color="primary" @click="openAddDialog" :loading="dialogLoading" v-if="authStore.canEdit">
            <v-icon left>mdi-plus</v-icon> Add Tenant
          </v-btn>
          <!-- <ExportButtons entity="tenants" filename="Tenants" :exclude="['id','created_at','updated_at']"></ExportButtons> -->
          <ExportButtons entity="tenants" filename="Tenants" :headers="headers"></ExportButtons>
        </v-card-title>
  
        <v-divider></v-divider>
  
        <v-data-table
          :headers="headers"
          :items="tenants"
          :search="search"
          item-value="id"
          class="elevation-1"
          :loading="tableLoading"
          loading-text="Loading tenants... Please wait"
          hover
        >
          
          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip location="top" text="View Details">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="openViewDialog(item)" color="info">
                mdi-eye-outline
              </v-icon>
            </template>
          </v-tooltip>

          <v-tooltip location="top" text="Edit Tenant">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="editItem(item)" color="secondary" v-if="authStore.canEdit">
                mdi-pencil
              </v-icon>
            </template>
          </v-tooltip>

          <v-tooltip location="top" text="Delete Tenant">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="deleteItemPrompt(item)" color="error" v-if="authStore.isAdmin">
                mdi-delete
              </v-icon>
            </template>
          </v-tooltip>

          </template>
  
          <template v-slot:[`item.sex`]="{ value }">
            {{ formatSex(value) }}
          </template>
  
          <template v-slot:[`no-data`]>
            <v-alert :value="true" color="info" icon="mdi-information-variant" class="ma-3">
              {{ tableLoading ? 'Fetching data...' : 'No tenants available or match your search.' }}
            </v-alert>
          </template>
        </v-data-table>
      </v-card>
  
      <v-dialog v-model="dialog" persistent max-width="600px" @update:modelValue="val => !val && closeDialog()">
        <v-card :loading="dialogLoading">
          <v-card-title class="tableheader white--text">
            <span class="text-h5">{{ formTitle }}</span>
          </v-card-title>
          <v-form ref="tenantForm" v-model="isFormValid" @submit.prevent="saveItem">
            <v-card-text class="mt-4">
              <v-container>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="editedItem.first_name"
                      label="First Name*"
                      variant="outlined"
                      :rules="[rules.required, rules.maxLength(255)]"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="editedItem.last_name"
                      label="Last Name*"
                      variant="outlined"
                      :rules="[rules.required, rules.maxLength(255)]"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedItem.sex"
                      :items="sexOptions"
                      item-title="text"
                      item-value="value"
                      label="Sex*"
                      variant="outlined"
                      :rules="[rules.required]"
                      required
                    ></v-select>
                  </v-col>
                  
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="editedItem.country_code"
                      label="Country Code (e.g. +256)"
                      variant="outlined"
                      :rules="[rules.countryCode, rules.maxLength(4)]"
                      prepend-inner-icon="mdi-earth"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="editedItem.phone"
                      label="Phone Number*"
                      variant="outlined"
                      :rules="[rules.required, rules.phone, rules.maxLength(15)]"
                      required
                      prepend-inner-icon="mdi-phone"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field v-model="editedItem.email" label="Email" variant="outlined" type="email"/>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field v-model="editedItem.id_or_passport_number"
                                  label="ID / Passport #" variant="outlined"/>
                  </v-col>

                  <v-col cols="12" md="6">
                                  <v-text-field
                      v-model="editedItem.date_of_birth"
                      label="Date of birth*"
                      type="date"
                      variant="outlined"
                      :rules="[rules.required, rules.validDate]"
                      required
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-select v-model="editedItem.status"
                              :items="statusOpts"
                              item-title="label" item-value="value"
                              label="Tenant status" variant="outlined"/>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field v-model="editedItem.emergency_contact_name"
                                  label="Emergency contact name" variant="outlined"/>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field v-model="editedItem.emergency_contact_phone"
                                  label="Emergency contact phone" variant="outlined"/>
                  </v-col>

                  <!-- <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedItem.company_id"
                      :items="companies"
                      item-title="name"
                      item-value="id"
                      label="Company"
                      variant="outlined"
                      class="mb-2"
                      :rules="[rules.required]"
                    />
                  </v-col> -->

                </v-row>
              </v-container>
              <small class="pl-3">*indicates required field</small>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey-darken-1" variant="text" @click="closeDialog" :disabled="dialogLoading">Cancel</v-btn>
              <v-btn color="primary" variant="elevated" type="submit" :loading="dialogLoading" :disabled="!isFormValid">Save</v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>
  
      <v-dialog v-model="viewDialog" max-width="800px" @update:modelValue="val => !val && closeViewDialog()">
      <v-card :loading="viewDialogLoading">
        <v-card-title class="tableheader black--text d-flex align-center">
          <!-- <v-icon left color="white" class="mr-2">mdi-home-account</v-icon> -->
          <span class="text-h5">Tenant Details: {{ viewItem?.first_name + ' ' + viewItem?.last_name }}</span>
          <v-spacer></v-spacer>
          <!-- <v-btn icon="mdi-close" variant="text" color="white" @click="closeViewDialog"></v-btn> -->
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text v-if="viewItem" class="pt-4">
          <v-list lines="two" density="compact">
            <!-- <v-list-item title="ID" :subtitle="viewItem.id"></v-list-item><v-divider inset></v-divider> -->
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">First Name:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.first_name }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Last Name:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.last_name }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Sex:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatSex(viewItem.sex) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Phone:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.phone }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Country Code:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.country_code }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Email:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.email }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">ID / Passport #:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.id_or_passport_number }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Date of Birth:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatDateForDisplay(viewItem.date_of_birth) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Emergency Contact Name:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.emergency_contact_name }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Emergency Contact Phone:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.emergency_contact_phone }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Status:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.status === 'active' ? 'Active' : 'Inactive' }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <!-- <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Company:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findName(companies, viewItem.company_id) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider> -->
          </v-list>
        </v-card-text>
        <v-card-text v-else class="text-center py-5">
            <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
            <p>Loading unit details...</p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="bg-grey-lighten-4 pa-3">
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="tonal" @click="closeViewDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

      <v-dialog v-model="deleteDialog" max-width="500px">
        <v-card :loading="dialogLoading">
          <v-card-title class="text-h5">Confirm Deletion</v-card-title>
          <v-card-text>Are you sure you want to delete this tenant: {{ itemToDelete?.first_name }} {{ itemToDelete?.last_name }}?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" text @click="closeDeleteDialog" :disabled="dialogLoading">Cancel</v-btn>
            <v-btn color="red-darken-1" text @click="confirmDelete" :loading="dialogLoading">Delete</v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
        {{ snackbar.message }}
        <template v-slot:actions>
          <v-btn color="white" variant="text" @click="snackbar.show = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, nextTick } from 'vue';
  import TenantService from '@/services/TenantService';
  import { useAuthStore } from '@/stores/auth'; 
  import { formatDateForDisplay } from '@/services/genFunctions';
  import ExportButtons from '@/components/ExportButtons.vue';
  // import CompanyService from '@/services/CompaniesService';
  // const companies = ref([]);

  const authStore = useAuthStore();
  const search = ref('');
  const tableLoading = ref(true);
  const dialog = ref(false);
  const dialogLoading = ref(false);
  const deleteDialog = ref(false);
  const tenantForm = ref(null);
  const isFormValid = ref(false);
  
  const viewDialog = ref(false);
  const viewDialogLoading = ref(false);
  const viewItem = ref(null);

  const snackbar = ref({ show: false, message: '', color: 'success', timeout: 3000 });
  const showSnackbar = (message, color = 'success', timeout = 3000) => {
    snackbar.value = { message, color, timeout, show: true };
  };
  
  const defaultItem = {
    id: null,
    first_name: '',
    last_name: '',
    sex: null,
    country_code: '',
    phone: '',
    email:'',date_of_birth:'',
    id_or_passport_number:'',
    emergency_contact_name:'',
    emergency_contact_phone:'',
    status:'active'
  };

  const statusOpts = [
    {label:'Active', value:'active'},
    {label:'Inactive', value:'inactive'}
  ];

  const editedItem = ref({ ...defaultItem });
  const itemToDelete = ref(null);
  
  const sexOptions = ref([
    { text: 'Male', value: 'M' },
    { text: 'Female', value: 'F' },
    { text: 'Other', value: 'O' }
  ]);
  
  const rules = {
    required: value => !!value || 'This field is required.',
    maxLength: (len) => value => (value || '').length <= len || `Max ${len} characters.`,
    phone: value => /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(value || '') || 'Invalid phone number format.',
    countryCode: value => (!value || /^\+\d{1,3}$/.test(value)) || 'Invalid country code (e.g. +256).'
  };
  
  const headers = ref([
    // { title: 'ID', key: 'id', align: 'start', sortable: true, width: '80px' },
    { title: 'First Name', key: 'first_name', sortable: true },
    { title: 'Last Name', key: 'last_name', sortable: true },
    { title: 'Sex', key: 'sex', sortable: true },
    { title: 'Country Code', key: 'country_code', sortable: false },
    { title: 'Phone', key: 'phone', sortable: true },
    { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '130px' }
  ]);
  
  const tenants = ref([]);
  
  const fetchTenants = async () => {
    tableLoading.value = true;
    try {
      tenants.value = await TenantService.getTenants();
    } catch (error) {
      console.error("Error fetching tenants:", error);
      showSnackbar('Failed to load tenants.', 'error');
      tenants.value = [];
    } finally {
      tableLoading.value = false;
    }
  };

  // const loadCompanies = async () => companies.value = await CompanyService.getCompanies();

  // const findName = (list, id) => {
  //   if (!list || !Array.isArray(list) || !id) return '�';
  //   const obj = list.find(i => i && i.id === id);
  //   return obj ? obj.name : `Unknown (ID: ${id})`;
  // };

  onMounted(async () => {
    // await loadCompanies();
    await fetchTenants();
  });
  
  const formatSex = (value) => {
    const option = sexOptions.value.find(opt => opt.value === value);
    return option ? option.text : value;
  };
  
  const formTitle = computed(() => !editedItem.value.id ? 'New Tenant' : 'Edit Tenant');
  
  const openAddDialog = () => {
    editedItem.value = { ...defaultItem };
    dialog.value = true;
    nextTick(() => tenantForm.value?.resetValidation());
  };
  
  const editItem = (item) => {
    editedItem.value = { ...item };
    dialog.value = true;
    nextTick(() => tenantForm.value?.resetValidation());
  };
  
  const deleteItemPrompt = (item) => {
    itemToDelete.value = { ...item };
    deleteDialog.value = true;
  };
  
  const closeDialog = async () => {
    dialog.value = false;
    await nextTick();
    editedItem.value = { ...defaultItem };
    tenantForm.value?.resetValidation();
    dialogLoading.value = false;
  };
  
  const closeDeleteDialog = () => {
    deleteDialog.value = false;
    nextTick(() => itemToDelete.value = null);
    dialogLoading.value = false;
  };
  
  const saveItem = async () => {
    if (!tenantForm.value) return;
    const { valid } = await tenantForm.value.validate();
    if (!valid) {
      showSnackbar('Please correct the form errors.', 'warning');
      return;
    }
    dialogLoading.value = true;
    try {
      if (editedItem.value.id) {
        const updatedTenant = await TenantService.updateTenant(editedItem.value.id, editedItem.value);
        const index = tenants.value.findIndex(t => t.id === updatedTenant.id);
        if (index > -1) tenants.value.splice(index, 1, updatedTenant);
        else await fetchTenants();
        showSnackbar('Tenant updated successfully!', 'success');
      } else {
        const newTenant = await TenantService.createTenant(editedItem.value);
        tenants.value.unshift(newTenant);
        showSnackbar('Tenant created successfully!', 'success');
      }
      closeDialog();
    } catch (error) {
      console.error("Error saving tenant:", error.response?.data?.error || error.message);
      showSnackbar(`Error: ${error.response?.data?.error || 'Could not save tenant.'}`, 'error', 5000);
    } finally {
      dialogLoading.value = false;
    }
  };
  
  const confirmDelete = async () => {
    if (!itemToDelete.value?.id) return;
    dialogLoading.value = true;
    try {
      await TenantService.deleteTenant(itemToDelete.value.id);
      tenants.value = tenants.value.filter(t => t.id !== itemToDelete.value.id);
      showSnackbar('Tenant deleted successfully!', 'success');
      closeDeleteDialog();
    } catch (error) {
      console.error("Error deleting tenant:", error);
      showSnackbar(`Error: ${error.response?.data?.error || 'Could not delete tenant.'}`, 'error', 5000);
    } finally {
      dialogLoading.value = false;
    }
  };

  const openViewDialog = async (item) => {
    viewDialogLoading.value = true;
    viewDialog.value = true;
    viewItem.value = null; 
    try {
      const fullTenantData = await TenantService.getTenant(item.id);
      viewItem.value = fullTenantData;
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to load tenant details.';
      showSnackbar(errorMsg, 'error');
      console.error("Error fetching tenant details for view:", error);
      viewDialog.value = false; 
    } finally {
      viewDialogLoading.value = false;
    }
  };

  const closeViewDialog = () => {
    viewDialog.value = false;
  };
  </script>
  
  <style scoped>
  .fill-height {
    min-height: calc(100vh - 64px); /* Adjusted for no footer on register page */
  }
  .primary--text { color: var(--v-theme-primary) !important; }
  .text-primary { color: var(--v-theme-primary) !important; }
  .v-card { border-top: 4px solid var(--v-theme-primary); }
  .v-toolbar-title { line-height: 1.5rem; }
  </style>